import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import { UserContext } from 'context/userContext';
import {
  ROTATION_CHOICES,
  readHeroRotation,
  saveHeroRotation,
} from 'lib/vipHeroRotation';
import {
  getMatchCrests,
  MatchCrests,
  teamLogoUrl,
} from 'services/api/matchDetailsService';
import { MatchInsightsModal } from 'components/MatchInsights/MatchInsightsModal';
import { SearchableOtherBets } from 'components/OtherBets/SearchableOtherBets';
import { LiveEvent } from 'types/LiveEvent';
import { SportIDs } from 'types/enums';
import { liveClock } from 'utils/sportEvent.utils';
import { VipIcon } from '../../VipChrome/VipIcons';
import styles from './VipLiveHero.module.css';

/** The match with the most markets, football first: the one most worth
 *  showing. It stays until it ends, so the card does not jump about. */
const pickHero = (events: Map<number, LiveEvent[]>, keep?: number | string) => {
  const football = events.get(SportIDs.SOCCER) ?? [];
  const list = football.length ? football : Array.from(events.values()).flat();
  return (
    list.find((x) => x.fi === keep) ??
    list.reduce<LiveEvent | undefined>(
      (best, x) => (!best || x.bets.length > best.bets.length ? x : best),
      undefined
    )
  );
};

/** The team's crest, or its initials while it has none. */
const Crest = ({ imageId, name }: { imageId?: string; name: string }) => {
  const [failed, setFailed] = useState<string>();
  const src = teamLogoUrl(imageId, 'm');
  if (src && failed !== src) {
    return (
      <span className={styles.crest}>
        <img
          src={src}
          alt=""
          onLoad={(e) => e.currentTarget.naturalWidth <= 1 && setFailed(src)}
          onError={() => setFailed(src)}
        />
      </span>
    );
  }
  const initials = name
    .split(/\s+/)
    .filter((x) => /^\p{L}/u.test(x))
    .slice(0, 2)
    .map((x) => x[0].toUpperCase())
    .join('');
  return <span className={styles.initials}>{initials}</span>;
};

const HeroCard = ({
  event,
  onOpen,
}: {
  event: LiveEvent;
  onOpen: () => void;
}) => {
  const { t } = useTranslation();
  const [crests, setCrests] = useState<MatchCrests>({});
  useEffect(() => {
    let alive = true;
    void getMatchCrests(event.fi).then((x) => alive && setCrests(x));
    return () => {
      alive = false;
    };
  }, [event.fi]);
  return (
    <button
      type="button"
      className={styles.hero}
      data-vip-dark
      onClick={onOpen}
    >
      <span className={styles.glow} />
      <span className={styles.live}>
        <i />
        LIVE
      </span>
      <span className={styles.row}>
        <span className={styles.team}>
          <Crest imageId={crests.home} name={event.home.name} />
          {event.home.name}
        </span>
        <span className={styles.board}>
          <b>
            {event.homeGoals}:{event.awayGoals}
          </b>
          <small>{liveClock(event, t)}</small>
        </span>
        <span className={styles.team}>
          <Crest imageId={crests.away} name={event.away.name} />
          {event.away.name}
        </span>
      </span>
      <span className={styles.sub}>
        {event.league} · {t('match_statistics_label')}
      </span>
    </button>
  );
};

/** The favourites, one card at a time: swiped, by the arrows and dots, or
 *  turning by themselves as the player set it. */
const FavoritesRail = ({
  favorites,
  player,
  paused,
  onOpen,
}: {
  favorites: LiveEvent[];
  player: number | undefined;
  paused: boolean;
  onOpen: (event: LiveEvent) => void;
}) => {
  const { t } = useTranslation();
  const rail = useRef<HTMLDivElement>(null);
  const [scrolled, setScrolled] = useState(0);
  const [touching, setTouching] = useState(false);
  const [menu, setMenu] = useState(false);
  const [seconds, setSeconds] = useState(() => readHeroRotation(player));
  const count = favorites.length;
  const index = Math.min(scrolled, count - 1);
  const many = count > 1;

  const goTo = useCallback((to: number) => {
    const el = rail.current;
    if (el) el.scrollTo({ left: to * el.clientWidth, behavior: 'smooth' });
  }, []);
  const stopped = !many || seconds === 0 || paused || touching || menu;
  useEffect(() => {
    if (stopped) return;
    const timer = setTimeout(() => goTo((index + 1) % count), seconds * 1000);
    return () => clearTimeout(timer);
  }, [stopped, index, count, seconds, goTo]);

  return (
    <div className={styles.carousel}>
      <div
        ref={rail}
        className={styles.rail}
        onScroll={(e) => {
          const el = e.currentTarget;
          setScrolled(Math.round(el.scrollLeft / el.clientWidth));
        }}
        onTouchStart={() => setTouching(true)}
        onTouchEnd={() => setTouching(false)}
        onTouchCancel={() => setTouching(false)}
      >
        {favorites.map((x) => (
          <div className={styles.slide} key={x.fi}>
            <HeroCard event={x} onOpen={() => onOpen(x)} />
          </div>
        ))}
      </div>
      <div className={styles.controls} data-vip-dark>
        <span className={styles.count}>
          <VipIcon name="star" size={12} />
          {index + 1}/{count}
        </span>
        {many && (
          <>
            <button
              type="button"
              className={styles.rotate}
              aria-label={t('vip_rotate_label')}
              aria-expanded={menu}
              onClick={() => setMenu(!menu)}
            >
              <VipIcon name="timer" size={14} />
              {seconds > 0 && `${seconds}s`}
            </button>
            <button
              type="button"
              className={classNames(styles.arrow, styles.arrowPrev)}
              aria-label={t('vip_slide_prev')}
              onClick={() => goTo((index - 1 + count) % count)}
            >
              <VipIcon name="chevron" size={16} />
            </button>
            <button
              type="button"
              className={styles.arrow}
              aria-label={t('vip_slide_next')}
              onClick={() => goTo((index + 1) % count)}
            >
              <VipIcon name="chevron" size={16} />
            </button>
          </>
        )}
        {menu && (
          <>
            <button
              type="button"
              className={styles.menuClose}
              aria-label={t('vip_rotate_label')}
              onClick={() => setMenu(false)}
            />
            <div className={styles.menu}>
              <b>{t('vip_rotate_label')}</b>
              <div>
                {ROTATION_CHOICES.map((x) => (
                  <button
                    type="button"
                    key={x}
                    aria-pressed={x === seconds}
                    onClick={() => {
                      setSeconds(x);
                      saveHeroRotation(player, x);
                      setMenu(false);
                    }}
                  >
                    {x === 0 ? t('vip_rotate_off') : `${x}s`}
                  </button>
                ))}
              </div>
            </div>
          </>
        )}
      </div>
      {many && (
        <div className={styles.dots}>
          {favorites.map((x, i) => (
            <button
              type="button"
              key={x.fi}
              aria-label={`${i + 1}/${count}`}
              aria-current={i === index}
              onClick={() => goTo(i)}
            />
          ))}
        </div>
      )}
    </div>
  );
};

/** VIP live page: the big scoreboard of the home's live card, or the player's
 *  favourites to go through. */
export const VipLiveHero = ({
  events,
  favorites,
  loading,
}: {
  events: Map<number, LiveEvent[]>;
  favorites: LiveEvent[];
  /** The first list is on its way: the card holds its place. */
  loading: boolean;
}) => {
  const { loggedUser } = useContext(UserContext);
  const player: number | undefined = loggedUser?.id;
  const [openFi, setOpenFi] = useState<number | string>();
  const kept = useRef<number | string>();
  const hero = pickHero(events, kept.current);
  kept.current = hero?.fi;
  const slides = favorites.length > 0 ? favorites : hero ? [hero] : [];
  if (slides.length === 0) {
    return loading ? <div className={styles.holder} /> : null;
  }
  const open = slides.find((x) => x.fi === openFi);
  return (
    <>
      {open && (
        <MatchInsightsModal
          fi={open.fi}
          onClose={() => setOpenFi(undefined)}
          liveOdds={<SearchableOtherBets match={open} bets={open.bets} />}
        />
      )}
      {favorites.length > 0 ? (
        <FavoritesRail
          key={player ?? 'guest'}
          favorites={favorites}
          player={player}
          paused={!!open}
          onOpen={(x) => setOpenFi(x.fi)}
        />
      ) : (
        <HeroCard event={slides[0]} onOpen={() => setOpenFi(slides[0].fi)} />
      )}
    </>
  );
};
